/**
 * Vault-level configuration.
 *
 * Exists because per-widget settings drift. Two `tasklist` widgets with
 * different `timezone` values roll over to "tomorrow" hours apart, and nothing
 * flags it — the configs are individually valid. One default in one place is
 * the fix; widgets may still override deliberately.
 */

import { z } from 'zod';
import { parseFile } from './markdown.js';
import { describeTimeZoneProblem } from './schema.js';
import type { VaultFS } from './ports.js';

export const CONFIG_PATH = 'config.md';

/**
 * One entry in the app-wide tab bar.
 *
 * `to` is a dashboard name, not a path — the bar navigates between screens the
 * router already knows how to resolve. External URLs are deliberately not
 * allowed here: a tab that leaves the app is a trapdoor, not navigation.
 */
const TabSchema = z.object({
  label: z.string().min(1),
  /** Dashboard name, resolving to `dashboards/<to>.md`. */
  to: z
    .string()
    .min(1)
    .refine((value) => !value.includes('/') && !value.includes('..'), {
      message: 'must be a bare dashboard name like "journal", not a path or URL',
    }),
  /** Optional glyph shown above the label. Any character — emoji, symbol, letter. */
  icon: z.string().optional(),
});

export type Tab = z.infer<typeof TabSchema>;

export const VaultConfigSchema = z.object({
  /**
   * The zone that decides what "today" means across the whole vault: which
   * daily file a `tasklist` opens, which day the week strip highlights, and the
   * date any agent should use when creating a dated file.
   */
  timezone: z
    .string()
    .default('America/New_York')
    .superRefine((value, ctx) => {
      const problem = describeTimeZoneProblem(value);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    }),
  /** Theme used by dashboards that don't name one. */
  theme: z.string().default('midnight'),
  /** Shown in the browser tab and on the install prompt. */
  title: z.string().default('HUD'),
  /**
   * App-wide bottom navigation. Empty (the default) renders no bar at all, so
   * vaults that predate this key are unaffected.
   *
   * Capped at five: past that the targets get too narrow to hit with a thumb,
   * and the answer is a nested screen rather than a sixth tab.
   */
  tabs: z.array(TabSchema).max(5).default([]),
});

export type VaultConfig = z.infer<typeof VaultConfigSchema>;

export const DEFAULT_VAULT_CONFIG: VaultConfig = VaultConfigSchema.parse({});

/**
 * Read `config.md`. A missing or invalid file is not fatal — the defaults are
 * usable, and a broken config should not stop the dashboard from rendering.
 */
export function parseVaultConfig(raw: string | undefined): {
  config: VaultConfig;
  error?: string;
} {
  if (raw === undefined) return { config: DEFAULT_VAULT_CONFIG };

  const parsed = parseFile(raw);
  if (parsed.error) {
    return { config: DEFAULT_VAULT_CONFIG, error: `Invalid YAML in ${CONFIG_PATH}: ${parsed.error}` };
  }

  const result = VaultConfigSchema.safeParse(parsed.data);
  if (!result.success) {
    return {
      config: DEFAULT_VAULT_CONFIG,
      error: `Invalid ${CONFIG_PATH}: ${result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`,
    };
  }

  return { config: result.data };
}

export async function loadVaultConfig(fs: VaultFS): Promise<VaultConfig> {
  try {
    return parseVaultConfig((await fs.read(CONFIG_PATH)).content).config;
  } catch {
    return DEFAULT_VAULT_CONFIG;
  }
}
